import * as React from "react";
import {
  Breadcrumb,
  BreadcrumbTemplateEnum,
  Breadcrumbs,
  LoadingSpinner,
  Page,
  PageHeader,
  PageHeaderLeft,
  PageHeaderRight,
  PageInner,
  PageSection
} from "@app/shared";
import { QESAlert, QESButton, WizardNavigationBar } from "@app/shared/qes-standards";
import { toast } from "react-toastify";
import { Link, Redirect, RouteComponentProps } from "react-router-dom";
import { SoftwareReleaseClient, SoftwareReleaseService } from "@app/software-releases";
import moment from "moment";
import { ColumnConfig, Waffles, wafflesKeyColumnName } from "@app/shared/waffles";
import { isEqual } from "lodash";
import { RoleRestricted, Roles } from "@app/user";
import { ModalLink } from "@app/shared/Modals";
import { QesIcons } from "@app/shared/icons/qesIcons";

interface SoftwareReleaseClientTableData {
  client: string;
  hold: string;
  [wafflesKeyColumnName]: string;
}

const columnConfig: ColumnConfig = {
  /* eslint-disable sort-keys */
  client: {
    displayText: "Client",
    testId: "client-name",
  },
  hold: {
    displayText: "Hold Software Release?",
    testId: "hold-software-release",
  },
  dateReleasedToClient: {
    displayText: "Date Released to Client",
    testId: "date-released-to-client"
  }
  /* eslint-enable sort-keys */
};

interface EditSoftwareReleaseProps {
  softwareReleaseId: string;
}
type PropsType = RouteComponentProps<EditSoftwareReleaseProps>;

export const SoftwareReleaseDetail: React.FC<React.PropsWithChildren<PropsType>> = (props: PropsType) => {

  const [error] = React.useState("");
  const [loading, setLoading] = React.useState(true);
  const [isDirty, setIsDirty] = React.useState(false);
  const [redirect, setRedirect] = React.useState("");
  const [submitting, setSubmitting] = React.useState(false);
  const [softwareReleaseId, setSoftwareReleaseId] = React.useState<number>();
  const [releaseVersion, setReleaseVersion] = React.useState("-1");
  const [releasePath, setReleasePath] = React.useState("");
  const [dateReleased, setDateReleased] = React.useState<Date>();
  const [softwareReleaseClientsTableData, setSoftwareReleaseClientsTableData] = React.useState<SoftwareReleaseClientTableData[]>([]);

  const [selectedSoftwareReleaseClientIds, setSelectedSoftwareReleaseClientIds] = React.useState<number[]>([]);
  const initialSoftwareReleaseClientIds = React.useRef<number[]>([]);

  React.useEffect(() => {
    setSoftwareReleaseId(parseInt(props.match.params.softwareReleaseId, 10))
  });

  React.useEffect(() => {
    if (softwareReleaseId !== undefined) {
      setLoading(true);

      const softwareReleaseService = new SoftwareReleaseService();
      softwareReleaseService.getById(softwareReleaseId).then(sr => {

        setReleaseVersion(`${sr.releaseYear}.${sr.releaseQuarter}`);
        setReleasePath(sr.releasePath);
        setDateReleased(sr.dateReleased);

        if (sr.dateReleased) {
          softwareReleaseService.getEligibleClients(softwareReleaseId).then(clients => {

            const releasedClientIds = clients.filter(c => c.isReleased).map(c => c.clientId).sort();

            setSoftwareReleaseClientsTableData(buildTableData(clients));

            initialSoftwareReleaseClientIds.current = {
              ...releasedClientIds
            };

            setSelectedSoftwareReleaseClientIds(releasedClientIds);
          });
        }

        setLoading(false);
      });
    }
  }, [softwareReleaseId]);

  const buildTableData = (data: SoftwareReleaseClient[]): SoftwareReleaseClientTableData[] => {

    return data.map<SoftwareReleaseClientTableData>((src) => {
      /* eslint-disable sort-keys */
      return {
        client: src.clientName,
        hold: src.holdSoftwareRelease ? "Y" : "N",
        [wafflesKeyColumnName]: src.clientId.toString(),
      };
      /* eslint-enable sort-keys */
    });
  }

  const breadcrumbs: (string | Breadcrumb)[] = React.useMemo(() => ([
    {
      iconClass: "fa-laptop",
      link: "/admin/software-releases",
      text: "QAS Software Releases",
    },
    loading ? "Loading..." : releaseVersion
  ]), [releaseVersion, loading]);

  const handleDelete = () => {
    setSubmitting(true);

    const softwareReleaseService = new SoftwareReleaseService();
    softwareReleaseService.delete(softwareReleaseId).then(() => {
      toast(<span>Software Release successfully deleted!</span>,
        {
          containerId: "z-index-9999",
          onOpen: () => {
            // there's no way to know whether the browser CAN go back, so we'll set redirect as a fallback
            window.browserHistory.goBack();
            setRedirect("/app/v2.0/admin/software-releases");
          },
          type: "success",
        },
      );
    }, (e) => {
      toast(<span>We encountered a problem deleting this Software Release: {e}.</span>,
        {
          containerId: "z-index-9999",
          onClose: () => {
            setSubmitting(false);
          },
          type: "error",
        },
      );
    });
  };


  const handleRelease = () => {
    setSubmitting(true);

    const softwareReleaseService = new SoftwareReleaseService();
    softwareReleaseService.release(softwareReleaseId).then(() => {
      toast(<span>Software Release successfully updated!</span>,
        {
          containerId: "z-index-9999",
          onOpen: () => {
            // there's no way to know whether the browser CAN go back, so we'll set redirect as a fallback
            window.browserHistory.goBack();
            setRedirect("/app/v2.0/admin/software-releases");
          },
          type: "success",
        },
      );
    }, (e) => {
      toast(<span>We encountered a problem releasing this Software Release: {e}.</span>,
        {
          containerId: "z-index-9999",
          onClose: () => {
            setSubmitting(false);
          },
          type: "error",
        },
      );
    });
  };

  const handleSubmit = () => {
    setSubmitting(true);

    const softwareReleaseService = new SoftwareReleaseService();
    softwareReleaseService.updateClientsByReleaseId(softwareReleaseId, selectedSoftwareReleaseClientIds).then(() => {
      toast(<span>Software Release successfully updated!</span>,
        {
          containerId: "z-index-9999",
          onOpen: () => {
            // there's no way to know whether the browser CAN go back, so we'll set redirect as a fallback
            window.browserHistory.goBack();
            setRedirect("/app/v2.0/admin/software-releases");
          },
          type: "success",
        },
      );
    }, (e) => {
      toast(<span>We encountered a problem updating this Software Release: {e}.</span>,
        {
          containerId: "z-index-9999",
          onClose: () => {
            setSubmitting(false);
          },
          type: "error",
        },
      );
    });
  };

  const handleSelectedClientsChange = (clientIds: string[]) => {
    setSelectedSoftwareReleaseClientIds(clientIds.map(cid => parseInt(cid)).sort());
  }

  React.useEffect(() => {
    const currentIsDirty = !isEqual(selectedSoftwareReleaseClientIds, Object.values(initialSoftwareReleaseClientIds.current));
    setIsDirty(currentIsDirty);

  }, [selectedSoftwareReleaseClientIds])

  const setRowStyle = (row: SoftwareReleaseClientTableData) => {
    return row.hold === "Y" ? "warning" : "";
  }

  const handleDownload = async () => {
    const softwareReleaseService = new SoftwareReleaseService();

    await softwareReleaseService.getEmailListCsv(softwareReleaseId);
  }

  if (redirect) {
    return <Redirect to={redirect} />;
  }

  return (
    <Page documentTitle="Software Release Detail - Admin">
      <Breadcrumbs template={BreadcrumbTemplateEnum.Admin} items={breadcrumbs} />
      <PageInner>
        <PageHeader title={`QAS Software Release ${releaseVersion}`} iconName={QesIcons.FontAwesome.Laptop} disableHR={false} >
          <PageHeaderLeft>
            <RoleRestricted allowedRoles={[Roles.SOFTWARE_LICENSE_ADMIN]}>
              <QESButton
                qesTestId="update-release__download"
                className="software-release__header-btn-link"
                qesType="secondary"
                onClick={() => { handleDownload(); }}
              >
                <i className="fa fa-download mr-1" />
                Download Email List
              </QESButton>
            </RoleRestricted>
          </PageHeaderLeft>
          <PageHeaderRight>
            {softwareReleaseId !== undefined && (
              <ModalLink
                content={{
                  body: "Are you sure you want to delete this Software Release?",
                  cancelButtonText: "Cancel",
                  confirmButtonAction: handleDelete,
                  confirmButtonText: "Delete",
                  confirmButtonType: "primary-danger",
                  title: "Delete Software Release",
                }}
                disabled={!!dateReleased}
                testId="software-releases__delete-modal"
                classNames="qes-button-secondary danger"
                hideBodyScroll={true}
              >
                Delete
              </ModalLink>
            )}
          </PageHeaderRight>
        </PageHeader>
        <div className="row mt-3">
          <div className="col-10">
            {loading && <div className="mt-3"><LoadingSpinner /></div>}
            {!(loading || submitting) && (
              <>
                <PageSection>
                  {!!error && <QESAlert type="danger" persist={true}>{error}</QESAlert>}
                  <div className="ml-3">
                    <div className="row">
                      <label className="qes-text-input__label mr-1">Download URL:</label>{releasePath || ""}
                    </div>
                    <div className="row align-middle">
                      <label className="qes-text-input__label mr-1">Date Released:</label>{!!dateReleased ? moment.utc(dateReleased).local().format("L") : "(not released)"}
                      {softwareReleaseId !== undefined && !dateReleased && (
                        <ModalLink
                          content={{
                            body: "Are you sure you want to release this Software Release?",
                            cancelButtonText: "Cancel",
                            confirmButtonAction: handleRelease,
                            confirmButtonText: "Release",
                            title: "Delete Software Release",
                          }}
                          disabled={!!dateReleased}
                          testId="software-releases__release-modal"
                          classNames="qes-button-secondary pt-0"
                          hideBodyScroll={true}
                        >
                          Release This Version
                        </ModalLink>
                      )}
                    </div>
                    {!!dateReleased && softwareReleaseClientsTableData &&
                      (
                        <>
                          <div className="row mt-3">
                            <label className="qes-text-input__label">Select Clients for This Release:</label>
                          </div>
                          <Waffles<SoftwareReleaseClientTableData>
                            columns={columnConfig}
                            data={softwareReleaseClientsTableData}
                            enablePagination={false}
                            fullWidth={true}
                            passThroughSorting={true}
                            testId="software-release-clients-table"
                            showCheckboxes={true}
                            selectedKeys={selectedSoftwareReleaseClientIds.map(cid => cid.toString())}
                            handleCheckboxChange={handleSelectedClientsChange}
                            rowDecorator={setRowStyle}
                          />
                        </>
                      )}
                  </div>
                </PageSection>
              </>
            )}
          </div>
        </div>
      </PageInner>
      <WizardNavigationBar
        leftSide={!submitting && <Link className="qes-button qes-button-secondary danger" to="/admin/software-releases">Cancel</Link>}
        rightSide={!submitting && !!dateReleased &&
          <>
            <QESButton
              qesType="primary"
              disabled={!isDirty}
              onClick={handleSubmit}
              qesTestId="update_release__submit"
            >
              Update Client Release Status
            </QESButton>
          </>
        }
      />
    </Page >
  );
}
